import { ChainType } from '@polaris/core/models'
import { featureFlagsAtom } from '@polaris/core/stores'
import { useAtomValue } from 'jotai'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { onboardingDialogAtom } from '@/src/stores/onboarding'
import { useWalletOptions } from '../wallet/use-wallet-options'
import { useActivateAccount } from './use-activate-account'

/**
 * Background account activation hook that automatically activates accounts
 * when wallets are connected, if the simple-account-activation feature flag is enabled.
 *
 * This hook runs in the background and triggers activation when:
 * - The simple-account-activation feature flag is enabled
 * - At least one wallet from cosmos, evm, or solana ecosystems is connected
 * - Account hasn't been activated yet
 */
export function useBackgroundActivation() {
  const featureFlags = useAtomValue(featureFlagsAtom)
  const onboardingDialog = useAtomValue(onboardingDialogAtom)
  const { connectedWallets } = useWalletOptions()
  const { mutateAsync: activateAccount, isPending } = useActivateAccount()
  const pathname = usePathname()

  // Keep track of which chain types we've already attempted activation for
  const activationAttempts = useRef(new Set<ChainType>())

  useEffect(() => {
    // Only proceed if the feature flag is enabled
    if (
      !featureFlags.simpleAccountActivation ||
      (onboardingDialog.mode === 'login' &&
        onboardingDialog.open &&
        featureFlags.newMpcAuthFlow) ||
      // We don't want to execute it inside oauth callbacks
      (pathname.startsWith('/auth') && featureFlags.newMpcAuthFlow)
    ) {
      return
    }

    // Don't attempt activation if already in progress
    if (isPending) {
      return
    }

    // Check for connected wallets in the supported ecosystems
    const supportedChainTypes = [
      ChainType.Cosmos,
      ChainType.Evm,
      ChainType.Solana,
    ] as const

    for (const chainType of supportedChainTypes) {
      const connectedWallet = connectedWallets[chainType]

      // If we have a connected wallet for this chain type and haven't attempted activation yet
      if (connectedWallet && !activationAttempts.current.has(chainType)) {
        // Mark this chain type as attempted
        activationAttempts.current.add(chainType)

        // Trigger activation in the background
        activateAccount({ chainType }).catch((error: unknown) => {
          console.error(`Background activation failed for ${chainType}:`, error)
          // Remove from attempts so we can retry later if needed
          activationAttempts.current.delete(chainType)
        })

        // Only activate for the first connected wallet to avoid multiple simultaneous activations
        break
      }
    }
  }, [
    featureFlags.simpleAccountActivation,
    featureFlags.newMpcAuthFlow,
    connectedWallets,
    activateAccount,
    isPending,
    onboardingDialog,
    pathname,
  ])

  // Reset activation attempts when all wallets are disconnected
  useEffect(() => {
    const hasAnyConnectedWallet = [
      ChainType.Cosmos,
      ChainType.Evm,
      ChainType.Solana,
    ].some((chainType) => connectedWallets[chainType])

    if (!hasAnyConnectedWallet) {
      activationAttempts.current.clear()
    }
  }, [connectedWallets])
}
